/**
 * Human-readable one-liners for a filter config — used by the widget tile's
 * "filters on" indicator popover. Field labels fall back to the generated
 * column label (the resolved field registry isn't available at the tile).
 */

import { parseFilterKey, getOperatorInfo } from '../filter-operators';
import { generateColumnLabel } from '../label-generation';
import type { BuildFilterConfig, UserFilterConfig } from './widget-config-types';

export interface FilterSummaryLine {
  /** e.g. "Application State" */
  label: string;
  /** e.g. "is any of: Complete, Abandoned" */
  text: string;
}

function describeValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value === true) return '';
  return String(value);
}

export function summarizeFilterConfig(
  config: BuildFilterConfig | UserFilterConfig | undefined
): FilterSummaryLine[] {
  if (!config) return [];
  const lines: FilterSummaryLine[] = [];
  // Only filters that actually constrain the query: the clause builder skips
  // empty/null/'' and empty-array values, so selected-but-unvalued filters
  // must not light the "filters on" indicator.
  for (const [key, value] of Object.entries(config.filterValues ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    const { baseKey, operator } = parseFilterKey(key);
    const opLabel = getOperatorInfo(operator)?.label ?? operator;
    const valueText = describeValue(value);
    lines.push({
      label: generateColumnLabel(baseKey),
      text: valueText ? `${opLabel}: ${valueText}` : opLabel,
    });
  }
  return lines;
}
